import { useMutation, type MutationKey } from "react-query";
import Axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { RemoteApiError } from "../useRemote/types";
import { FleetAttachment } from "../../../components/FleetManagement/types/FleetManagement.types";

type UploadFleetAttachmentPayload = {
  vehicleId: number;
  attachment: FleetAttachment;
};

/**
 * Upload a single fleet attachment (POST /fleet/vehicles/:id/attachments).
 * Backend expects multipart form-data with fields:
 * - file: binary
 * - description: string | null
 * - isAdminOnly: boolean flag
 */
export function useRemoteUploadFleetAttachment() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();

  return useMutation<void, RemoteApiError, UploadFleetAttachmentPayload>(
    ["fleet", "upload-attachment"] as MutationKey,
    async ({ vehicleId, attachment }: UploadFleetAttachmentPayload): Promise<void> => {
      const { file, isAdminOnly } = attachment;

      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append("file", file, file.name);
      // For now, use filename as description; BE accepts null/empty as well.
      formData.append("description", file.name);
      formData.append("isAdminOnly", isAdminOnly ? "true" : "false");

      try {
        await Axios.post(`/fleet/vehicles/${vehicleId}/attachments`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
        snackbar.enqueueSnackbar(t("Successfully uploaded attachment"), { variant: "success" });
      } catch (error) {
        snackbar.enqueueSnackbar(t("Failed to upload attachment"), { variant: "error" });
        throw error;
      }
    }
  );
}
